// The sport switcher's logic, lifted from components/SportSwitcher (§4.5).
// Presentation (pills, glyphs) belongs to the template; which sports exist,
// whether the switcher shows on this route, and what a pick does are core.
//
// Restoring the fan's stored sport is NOT here: it is SportProvider's job
// (P0, §6.0). `set` persists the pick.

import { useMemo } from 'react'
import { matchPath, useLocation } from 'react-router-dom'
import { useLeague } from '@/contexts/LeagueContext'
import { sportDisplayName, useSportContext } from '@/contexts/SportContext'
import type { Option } from '../template/pages'
import { tenantSportIds } from '@/contexts/storedSport'

// Detail / slug-based routes where swapping the sport mid-page would 404 (the
// entity belongs to one sport). The switcher is hidden there; fans navigate
// up to a list page first. Mirrors components/SportSwitcher.tsx.
export const SPORT_SWITCHER_HIDDEN_ON = [
  '/players/:id',
  '/players/:id/*',
  '/teams/:id',
  '/teams/:id/*',
  '/game/:id',
  '/game/:id/*',
  '/games/:id',
  '/games/:id/*',
  '/schedule/:id',
  '/pages/:slug',
  '/live/:slug',
  '/venues/:id',
  '/sponsor/:id',
  '/obs/*',
  '/scorebug/*',
  '/share/*',
] as const

export function sportSwitcherHiddenOn(pathname: string): boolean {
  return SPORT_SWITCHER_HIDDEN_ON.some((pat) => matchPath({ path: pat, end: pat.endsWith('*') ? false : true }, pathname))
}

export interface SiteSportSwitcher {
  /** 2+ sports on the tenant and not a detail/slug route. */
  visible: boolean
  /** Sorted sport ids with display names. */
  options: Option<string>[]
  active: string
  /** Switch and persist the fan's pick for this host. */
  set: (id: string) => void
}

export function useSportSwitcher(): SiteSportSwitcher {
  const { league } = useLeague()
  const { activeSportId, setActiveSport } = useSportContext()
  const { pathname } = useLocation()
  // #568: the tenant's public sports (a disabled/archived sport is not offered).
  const sports = useMemo(() => tenantSportIds(league), [league])
  return {
    visible: sports.length >= 2 && !sportSwitcherHiddenOn(pathname),
    options: sports.map((id) => ({ value: id, label: sportDisplayName(id) })),
    active: activeSportId,
    set: (id: string) => setActiveSport(id, { persist: true }),
  }
}
